import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, Save, Download, Copy, Library, Search, Filter, Check, Palette, Plus, RefreshCw, Eye, Heart, Table2, Image as ImageIcon, Pencil, ChevronDown, ChevronUp, Lock, Unlock, Shuffle, Upload, X, Contrast, Sparkles } from 'lucide-react';
import { ColorPreset, Color, TemplatePreset } from './types';
import { ColorSwatch } from './ColorSwatch';
import { CopyButton } from './CopyButton';
import { generateId } from './utils';
import { colorPresetService } from '@/lib/colorPresetService';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  extractPalette,
  contrastRatio,
  wcagLevel,
  hexToRgbValues,
  type PaletteResult,
  type WcagLevel,
} from '@/lib/imageColorExtractor';

import { HexColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  getColorValue,
  generateShades,
  generateTints,
  generateTransparencies,
  getTransparencyColorValue,
  parseColorValue,
} from './utils';

import { AnimatePresence, motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DropAnimation,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import { toast } from 'sonner';

type ActiveView = 'editor' | 'browse';

interface PresetEditorProps {
  preset: ColorPreset | null;
  onSave: (preset: ColorPreset) => Promise<void>;
  onCancel: () => void;
  embedded?: boolean;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  getCssRef?: React.MutableRefObject<(() => string) | null>;
  defaultView?: ActiveView;
  onTemplateApplied?: () => void;
  onChange?: () => void;
}

const DesignerColorCard: React.FC<{
  color: Color;
  index: number;
  isEditMode: boolean;
  isLocked: boolean;
  onColorChange: (id: string, updates: Partial<Color>) => void;
  toggleLock: (index: number) => void;
  onRemove?: (id: string) => void;
  onAddBlended?: (index: number, leftHex: string, rightHex: string) => void;
  isLast?: boolean;
  nextColor?: string;
}> = ({ color, index, isEditMode, isLocked, onColorChange, toggleLock, onRemove, onAddBlended, isLast, nextColor }) => {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [clickPos, setClickPos] = React.useState<{ x: number, y: number } | null>(null);
  const [format, setFormat] = React.useState<'oklch' | 'hex' | 'rgb' | 'hsl'>(
    (color.format as 'oklch' | 'hex' | 'rgb' | 'hsl') || 'oklch'
  );

  React.useEffect(() => {
    setFormat((color.format as 'oklch' | 'hex' | 'rgb' | 'hsl') || 'oklch');
  }, [color.format, color.id]);

  const r = parseInt(color.hex.slice(1, 3), 16) || 0;
  const g = parseInt(color.hex.slice(3, 5), 16) || 0;
  const b = parseInt(color.hex.slice(5, 7), 16) || 0;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  const isLight = brightness > 165;
  const textColor = isLight ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.65)';
  const valueColor = isLight ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)';
  const btnBase = isLight
    ? 'hover:bg-black/10 text-black/50 hover:text-black/80'
    : 'hover:bg-white/10 text-white/50 hover:text-white/90';

  const handleFormatChange = (newFormat: 'oklch' | 'hex' | 'rgb' | 'hsl') => {
    setFormat(newFormat);
    onColorChange(color.id, { format: newFormat });
  };

  const formats: ('oklch' | 'hex' | 'rgb' | 'hsl')[] = ['oklch', 'hex', 'rgb', 'hsl'];
  const handleCycleFormat = (direction: 'up' | 'down') => {
    const currentIndex = formats.indexOf(format);
    let newIndex = currentIndex;
    if (direction === 'up') {
      newIndex = currentIndex === 0 ? formats.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === formats.length - 1 ? 0 : currentIndex + 1;
    }
    handleFormatChange(formats[newIndex]);
  };

  return (
    <motion.div
      key={color.id}
      layout
      initial={{ opacity: 0, flexGrow: 0, minWidth: 0 }}
      animate={{ opacity: 1, flexGrow: 1, minWidth: 0 }}
      exit={{ opacity: 0, flexGrow: 0, minWidth: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`h-full flex-1 flex flex-col group relative hover:z-20 ${isEditMode ? 'cursor-pointer' : ''}`}
      style={{ backgroundColor: color.hex }}
      onClick={(e) => { 
        if (isEditMode) {
          const rect = e.currentTarget.getBoundingClientRect();
          setClickPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          setIsPickerOpen(true); 
        } 
      }}
    >
      {/* Anchor div for Popover */}
      {isEditMode && (
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <div
              className="absolute pointer-events-none w-0 h-0"
              style={{ left: clickPos?.x || '50%', top: clickPos?.y || '50%' }}
            />
          </PopoverTrigger>
          <PopoverContent 
            className="w-80 p-4 bg-card border-border text-foreground shadow-2xl" 
            side="right" 
            align="center" 
            sideOffset={16}
            onClick={(e) => e.stopPropagation()}
          >
            <HexColorPicker
              color={color.hex}
              onChange={(newColor) => onColorChange(color.id, { hex: newColor.toUpperCase() })}
              style={{ width: '100%' }}
            />
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={getColorValue(color.hex, format)}
                  onChange={(e) => {
                    const parsedHex = parseColorValue(e.target.value, format);
                    if (parsedHex) {
                      onColorChange(color.id, { hex: parsedHex });
                    }
                  }}
                  onBlur={(e) => {
                    const parsedHex = parseColorValue(e.target.value, format);
                    if (!parsedHex) {
                      e.target.value = getColorValue(color.hex, format);
                    }
                  }}
                  placeholder={format === 'hex' ? '#000000' : format === 'rgb' ? 'rgb(0, 0, 0)' : 'hsl(0, 0%, 0%)'}
                  className="font-mono text-sm flex-1 bg-background text-foreground border-border"
                />
                <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-9 shrink-0 select-none">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                    {format}
                  </span>
                  <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                    <button
                      type="button"
                      onClick={() => handleCycleFormat('up')}
                      className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                      title="Previous format"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCycleFormat('down')}
                      className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                      title="Next format"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
      {/* Center: copy and lock icons */}
      <div className="flex-1 flex flex-col items-center justify-center gap-2">
        {isEditMode && onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(color.id); }}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 ${btnBase}`}
            title="Remove color"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {isEditMode && (
          <button
            onClick={(e) => { e.stopPropagation(); toggleLock(index); }}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${
              isLocked
                ? isLight
                  ? 'text-black/80 bg-black/15 opacity-100'
                  : 'text-white bg-white/20 opacity-100'
                : `opacity-0 group-hover:opacity-100 ${btnBase}`
            }`}
            title={isLocked ? 'Unlock color' : 'Lock color'}
          >
            {isLocked ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
          </button>
        )}
        <div onClick={(e) => e.stopPropagation()}>
          <CopyButton
            value={getColorValue(color.hex, format)}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 ${btnBase} bg-transparent border-0 shadow-none hover:shadow-none`}
            iconClassName="w-5 h-5"
          />
        </div>
      </div>

      {isEditMode && !isLast && onAddBlended && nextColor && (
        <div className="absolute right-0 top-0 bottom-0 w-12 translate-x-1/2 z-10 flex items-center justify-center group/edge">
          <button
            onClick={(e) => { e.stopPropagation(); onAddBlended(index, color.hex, nextColor); }}
            className="w-8 h-8 rounded-full bg-background border border-border shadow-md flex items-center justify-center text-foreground hover:bg-accent hover:scale-110 transition-all opacity-0 group-hover/edge:opacity-100"
            title="Add color"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Bottom: hex + copy + picker, then name */}
      <div className="px-4 pb-6 flex flex-col items-center gap-1">
        <div className="flex items-center justify-center gap-1.5 w-full px-2" style={{ color: valueColor }}>
          <span
            className="text-[12px] font-bold font-mono tracking-wider select-all cursor-pointer hover:opacity-80 transition-opacity whitespace-nowrap overflow-hidden text-ellipsis"
            onClick={(e) => {
              e.stopPropagation();
              handleCycleFormat('down');
            }}
            title="Click to change format"
          >
            {getColorValue(color.hex, format)}
          </span>
          <div onClick={(e) => e.stopPropagation()} className="shrink-0 flex items-center">
            <CopyButton
              value={getColorValue(color.hex, format)}
              className="p-1 h-auto w-auto bg-transparent border-0 shadow-none opacity-60 hover:opacity-100 transition-opacity rounded flex items-center justify-center text-inherit hover:bg-black/10 dark:hover:bg-white/10"
              iconClassName="w-3.5 h-3.5"
            />
          </div>
        </div>

        {isEditMode ? (
          <input
            type="text"
            value={color.name}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onColorChange(color.id, { name: e.target.value })}
            className="text-[11px] font-bold uppercase tracking-widest text-center leading-tight bg-transparent border-none focus:outline-none focus:ring-0 w-full select-text"
            style={{ color: textColor }}
          />
        ) : (
          <span
            className="text-[11px] font-bold uppercase tracking-widest text-center leading-tight select-none"
            style={{ color: textColor, wordBreak: 'break-word' }}
          >
            {color.name}
          </span>
        )}
      </div>
    </motion.div>
  );
};

export const PresetEditor: React.FC<PresetEditorProps> = ({
  preset,
  onSave,
  onCancel,
  embedded = false,
  saveRef,
  getCssRef,
  defaultView = 'editor',
  onTemplateApplied,
  onChange,
}) => {
  const [activeView, setActiveView] = React.useState<ActiveView>(defaultView);
  const [isEditMode, setIsEditMode] = React.useState(!preset || embedded);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [isExtractOpen, setIsExtractOpen] = React.useState(false);
  const [isDesignerModalOpen, setIsDesignerModalOpen] = React.useState(false);
  const [isContrastModalOpen, setIsContrastModalOpen] = React.useState(false);
  const [extractPreview, setExtractPreview] = React.useState<string | null>(null);
  const [extractPaletteResult, setExtractPaletteResult] = React.useState<PaletteResult | null>(null);
  const [extractSeed, setExtractSeed] = React.useState(0);
  const [extractAnalyzing, setExtractAnalyzing] = React.useState(false);
  const [isExtractApplying, setIsExtractApplying] = React.useState(false);
  const extractImgRef = React.useRef<HTMLImageElement | null>(null);
  const extractInputRef = React.useRef<HTMLInputElement>(null);
  const [templateToPreview, setTemplateToPreview] = React.useState<TemplatePreset | null>(null);
  const [previewFormat, setPreviewFormat] = React.useState<'hex' | 'rgb' | 'hsl' | 'oklch'>('oklch');
  const [browseSearchQuery, setBrowseSearchQuery] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [lockedIndices, setLockedIndices] = React.useState<Set<number>>(new Set());
  const [dbTemplates, setDbTemplates] = React.useState<TemplatePreset[]>([]);
  const [designerDraftPreset, setDesignerDraftPreset] = React.useState<ColorPreset | null>(null);

  // Infinite scroll for browse tab
  const [browseVisibleCount, setBrowseVisibleCount] = React.useState(12);
  const CARDS_PER_PAGE = 12;
  const [isBrowseLoadingMore, setIsBrowseLoadingMore] = React.useState(false);

  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const loadingMoreRef = React.useRef(isBrowseLoadingMore);
  loadingMoreRef.current = isBrowseLoadingMore;
  const hasMoreRef = React.useRef(false);

  const browseSentinelRef = React.useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (node) {
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
          setIsBrowseLoadingMore(true);
          setTimeout(() => {
            setBrowseVisibleCount(prev => prev + CARDS_PER_PAGE);
            setIsBrowseLoadingMore(false);
          }, 400);
        }
      }, { root: null, rootMargin: '200px', threshold: 0.1 });
      observerRef.current.observe(node);
    }
  }, [CARDS_PER_PAGE]);

  React.useEffect(() => {
    if (defaultView) {
      setActiveView(defaultView);
    }
  }, [defaultView]);

  React.useEffect(() => {
    setBrowseVisibleCount(12);
  }, [browseSearchQuery, selectedCategory]);

  React.useEffect(() => {
    colorPresetService.getAllTemplates().then(setDbTemplates).catch(() => {});
  }, []);

  const [currentPreset, setCurrentPreset] = React.useState<ColorPreset>(
    preset || {
      id: generateId(),
      name: 'New Color Preset',
      colors: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  );

  const updatePresetState = React.useCallback((updater: (prev: ColorPreset) => ColorPreset) => {
    if (isDesignerModalOpen) {
      setDesignerDraftPreset(prev => prev ? updater(prev) : null);
    } else {
      setCurrentPreset(updater);
    }
    onChange?.();
  }, [isDesignerModalOpen, onChange]);

  // Expose save function to parent via ref
  React.useEffect(() => {
    if (saveRef) {
      saveRef.current = async () => {
        await onSave(currentPreset);
      };
    }
    return () => {
      if (saveRef) saveRef.current = null;
    };
  }, [saveRef, currentPreset, onSave]);

  // Expose CSS generator to parent via ref
  React.useEffect(() => {
    if (getCssRef) {
      getCssRef.current = () => {
        let css = `  /* Color Variables: ${currentPreset.name} */\n`;
        currentPreset.colors.forEach(color => {
          const colorFormat = color.format || 'oklch';
          const baseValue = getColorValue(color.hex, colorFormat);
          const varName = color.variable || '--color-' + color.name.toLowerCase().replace(/\\s+/g, '-');
          css += `  ${varName}: ${baseValue};\n`;

          if (color.shadesEnabled && color.shadesCount && color.shadesCount > 0) {
            const shades = color.customShades || generateShades(color.hex, Math.min(color.shadesCount, 5));
            shades.forEach(shade => {
              css += `  ${varName}${shade.variable}: ${getColorValue(shade.hex, colorFormat)};\n`;
            });
          }
          if (color.tintsEnabled && color.tintsCount && color.tintsCount > 0) {
            const tints = color.customTints || generateTints(color.hex, Math.min(color.tintsCount, 5));
            tints.forEach(tint => {
              css += `  ${varName}${tint.variable}: ${getColorValue(tint.hex, colorFormat)};\n`;
            });
          }
          if (color.transparencyEnabled && color.transparencyCount && color.transparencyCount > 0) {
            const transparencyFormat = colorFormat;
            const transparencies = generateTransparencies(color.hex, Math.min(color.transparencyCount, 10));
            transparencies.forEach(trans => {
              css += `  ${varName}${trans.variable}: ${getTransparencyColorValue(trans.hex, trans.opacity, transparencyFormat)};\n`;
            });
          }
        });
        return css;
      };
    }
    return () => {
      if (getCssRef) getCssRef.current = null;
    };
  }, [getCssRef, currentPreset]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: { active: { opacity: '0.5' } },
    }),
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (active.id !== over?.id) {
      setCurrentPreset(prev => {
        const oldIndex = prev.colors.findIndex(c => c.id === active.id);
        const newIndex = prev.colors.findIndex(c => c.id === over?.id);
        return { ...prev, colors: arrayMove(prev.colors, oldIndex, newIndex), updatedAt: new Date().toISOString() };
      });
    }
  };

  const addColorAt = (index: number) => {
    const newColor: Color = {
      id: generateId(),
      name: 'New Color',
      hex: '#6366F1',
      variable: '--new-color',
      format: 'oklch',
    };
    updatePresetState(prev => ({
      ...prev,
      colors: [...prev.colors.slice(0, index), newColor, ...prev.colors.slice(index)],
      updatedAt: new Date().toISOString(),
    }));
  };

  const updateColor = (id: string, updates: Partial<Color>) => {
    updatePresetState(prev => ({
      ...prev,
      colors: prev.colors.map(c => c.id === id ? { ...c, ...updates } : c),
      updatedAt: new Date().toISOString(),
    }));
  };

  const removeColor = (id: string) => {
    updatePresetState(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c.id !== id),
      updatedAt: new Date().toISOString(),
    }));
  };

  const addBlendedColor = async (index: number, leftHex: string, rightHex: string) => {
    const blendHex = (c1: string, c2: string) => {
      const hex1 = c1.replace('#', '');
      const hex2 = c2.replace('#', '');
      const r1 = parseInt(hex1.substring(0, 2), 16);
      const g1 = parseInt(hex1.substring(2, 4), 16);
      const b1 = parseInt(hex1.substring(4, 6), 16);
      const r2 = parseInt(hex2.substring(0, 2), 16);
      const g2 = parseInt(hex2.substring(2, 4), 16);
      const b2 = parseInt(hex2.substring(4, 6), 16);
      
      const r = Math.round((r1 + r2) / 2).toString(16).padStart(2, '0');
      const g = Math.round((g1 + g2) / 2).toString(16).padStart(2, '0');
      const b = Math.round((b1 + b2) / 2).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`.toUpperCase();
    };

    const blendedHex = blendHex(leftHex, rightHex);
    const newId = generateId();
    
    const newColor: Color = {
      id: newId,
      name: 'Loading...',
      hex: blendedHex,
      variable: '--blended-color',
      format: 'oklch',
    };
    
    updatePresetState(prev => ({
      ...prev,
      colors: [...prev.colors.slice(0, index + 1), newColor, ...prev.colors.slice(index + 1)],
      updatedAt: new Date().toISOString(),
    }));

    try {
      const res = await fetch(`https://www.thecolorapi.com/id?hex=${blendedHex.replace('#', '')}`);
      const data = await res.json();
      const name = data.name.value;
      const slugify = (text: string) => '--' + text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
      
      updatePresetState(prev => ({
        ...prev,
        colors: prev.colors.map(c => c.id === newId ? { ...c, name, variable: slugify(name) } : c),
        updatedAt: new Date().toISOString(),
      }));
    } catch (e) {
      updatePresetState(prev => ({
        ...prev,
        colors: prev.colors.map(c => c.id === newId ? { ...c, name: 'Blended Color' } : c),
      }));
    }
  };

  const handleSave = async () => {
    if (!currentPreset.name.trim() || currentPreset.colors.length === 0) {
      toast.error('Add at least one color before saving.');
      return;
    }
    setIsSaving(true);
    try {
      await onSave(currentPreset);
      setIsEditMode(false);
    } catch {
      // error handling is done in the parent
    } finally {
      setIsSaving(false);
    }
  };

  const generateCSSPreview = () => {
    let cssVariables = '';
    currentPreset.colors.forEach(color => {
      const colorFormat = color.format || 'oklch';
      const baseValue = getColorValue(color.hex, colorFormat);
      cssVariables += `  ${color.variable}: ${baseValue};\n`;
      if (color.shadesEnabled && color.shadesCount && color.shadesCount > 0) {
        const shades = color.customShades || generateShades(color.hex, Math.min(color.shadesCount, 5));
        shades.forEach(shade => { cssVariables += `  ${color.variable}${shade.variable}: ${getColorValue(shade.hex, colorFormat)};\n`; });
      }
      if (color.tintsEnabled && color.tintsCount && color.tintsCount > 0) {
        const tints = color.customTints || generateTints(color.hex, Math.min(color.tintsCount, 5));
        tints.forEach(tint => { cssVariables += `  ${color.variable}${tint.variable}: ${getColorValue(tint.hex, colorFormat)};\n`; });
      }
      if (color.transparencyEnabled && color.transparencyCount && color.transparencyCount > 0) {
        const transparencyFormat = colorFormat;
        const transparencies = generateTransparencies(color.hex, Math.min(color.transparencyCount, 10));
        transparencies.forEach(trans => { cssVariables += `  ${color.variable}${trans.variable}: ${getTransparencyColorValue(trans.hex, trans.opacity, transparencyFormat)};\n`; });
      }
    });
    return `:root {\n${cssVariables}}`;
  };

  const generateJSONPreview = () => JSON.stringify(currentPreset, null, 2);

  const downloadCSS = () => {
    const blob = new Blob([generateCSSPreview()], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentPreset.name.replace(/\s+/g, '-').toLowerCase()}.css`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const downloadJSON = () => {
    const blob = new Blob([generateJSONPreview()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentPreset.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  const toggleLock = (index: number) => {
    setLockedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleGlobalFormatChange = (direction: 'up' | 'down') => {
    const formats = ['hex', 'rgb', 'hsl', 'oklch'] as const;
    const currentFormat = currentPreset.colors[0]?.format || 'oklch';
    const currentIndex = formats.indexOf(currentFormat);
    const nextIndex = direction === 'up' 
      ? (currentIndex + 1) % formats.length 
      : (currentIndex - 1 + formats.length) % formats.length;
    const newFormat = formats[nextIndex];
    
    updatePresetState(prev => ({
      ...prev,
      colors: prev.colors.map(c => ({ ...c, format: newFormat as any })),
      updatedAt: new Date().toISOString()
    }));
  };

  const generateAPIPalette = React.useCallback(async () => {
    setIsGenerating(true);
    try {
      const h = Math.floor(Math.random() * 360);
      const s = 60 + Math.floor(Math.random() * 30);
      const l = 45 + Math.floor(Math.random() * 15);

      const hslToHex = (h: number, s: number, l: number) => {
        l /= 100;
        const a = s * Math.min(l, 1 - l) / 100;
        const f = (n: number) => {
          const k = (n + h / 30) % 12;
          const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
          return Math.round(255 * color).toString(16).padStart(2, '0');
        };
        return `${f(0)}${f(8)}${f(4)}`.toUpperCase();
      };

      const primaryHex = hslToHex(h, s, l);
      const darkHex = hslToHex(h, s, 10);
      const lightHex = hslToHex(h, s, 96);

      const [schemeRes, darkRes, lightRes] = await Promise.all([
        fetch(`https://www.thecolorapi.com/scheme?hex=${primaryHex}&mode=analogic-complement&count=3`),
        fetch(`https://www.thecolorapi.com/id?hex=${darkHex}`),
        fetch(`https://www.thecolorapi.com/id?hex=${lightHex}`)
      ]);

      const schemeData = await schemeRes.json();
      const darkData = await darkRes.json();
      const lightData = await lightRes.json();

      const slugify = (text: string) => '--' + text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

      const rawColors = [
        { hex: schemeData.colors[0].hex.value, name: schemeData.colors[0].name.value },
        { hex: schemeData.colors[1].hex.value, name: schemeData.colors[1].name.value },
        { hex: schemeData.colors[2].hex.value, name: schemeData.colors[2].name.value },
        { hex: `#${lightHex}`, name: lightData.name.value },
        { hex: `#${darkHex}`, name: darkData.name.value },
      ];

      const newColors: Color[] = rawColors.map(c => ({
        id: generateId(),
        name: c.name,
        hex: c.hex.startsWith('#') ? c.hex : `#${c.hex}`,
        variable: slugify(c.name),
        format: 'oklch' as const,
      }));

      updatePresetState(prev => ({
        ...prev,
        colors: newColors.map((newColor, i) => {
          if (lockedIndices.has(i) && prev.colors[i]) return prev.colors[i];
          if (prev.colors[i]) return { ...newColor, id: prev.colors[i].id };
          return newColor;
        }),
        updatedAt: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Failed to generate palette:', error);
      toast.error('Failed to fetch palette. Showing random colors.');
      const randomHex = () => '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0').toUpperCase();
      const names = ['Cobalt', 'Coral', 'Amber', 'Pearl', 'Obsidian'];
      updatePresetState(prev => ({
        ...prev,
        colors: names.map((name, i) => {
          if (lockedIndices.has(i) && prev.colors[i]) return prev.colors[i];
          const newColor = {
            id: generateId(),
            name,
            hex: randomHex(),
            variable: `--${name.toLowerCase()}`,
            format: 'oklch' as const,
          };
          if (prev.colors[i]) return { ...newColor, id: prev.colors[i].id };
          return newColor;
        }),
        updatedAt: new Date().toISOString(),
      }));
    } finally {
      setIsGenerating(false);
    }
  }, [lockedIndices, updatePresetState]);

  React.useEffect(() => {
    if (!preset && currentPreset.colors.length === 0) {
      generateAPIPalette();
    }
  }, [generateAPIPalette, preset, currentPreset.colors.length]);

  const categories = React.useMemo(() => {
    const cats = Array.from(new Set(dbTemplates.map(t => t.category))).sort();
    return ['All', ...cats];
  }, [dbTemplates]);

  const categoryCounts = React.useMemo(() => {
    const counts: Record<string, number> = { All: dbTemplates.length };
    dbTemplates.forEach(t => {
      counts[t.category] = (counts[t.category] || 0) + 1;
    });
    return counts;
  }, [dbTemplates]);

  const filteredTemplates = React.useMemo(() => {
    return dbTemplates.filter(template => {
      const matchSearch = template.name.toLowerCase().includes(browseSearchQuery.toLowerCase()) ||
        template.category.toLowerCase().includes(browseSearchQuery.toLowerCase());
      const matchCategory = selectedCategory === 'All' || template.category === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [dbTemplates, browseSearchQuery, selectedCategory]);

  const displayedTemplates = React.useMemo(() => {
    return filteredTemplates.slice(0, browseVisibleCount);
  }, [filteredTemplates, browseVisibleCount]);

  const browseHasMore = browseVisibleCount < filteredTemplates.length;
  hasMoreRef.current = browseHasMore;

  const applyTemplate = (template: TemplatePreset) => {
    setCurrentPreset(prev => ({
      ...prev,
      colors: template.colors.map(c => ({
        id: generateId(),
        name: c.name,
        hex: c.hex,
        variable: c.variable,
        format: c.format || 'oklch',
      })),
      updatedAt: new Date().toISOString(),
    }));
    setActiveView('editor');
    if (onTemplateApplied) {
      onTemplateApplied();
    }
  };

  const designerColors = (isDesignerModalOpen && designerDraftPreset) ? designerDraftPreset.colors : currentPreset.colors;

  return (
    <div className={`${embedded ? 'flex-1' : 'h-screen'} flex flex-col bg-background overflow-hidden`}>

      {/* ── Persistent Header ──────────────────────────────────────────── */}
      {!embedded && (
        <header className="h-16 border-b border-border flex items-center justify-between px-6 bg-background/80 backdrop-blur-md z-30 sticky top-0 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl w-10 h-10 hover:bg-accent transition-all active:scale-95 text-muted-foreground hover:text-foreground shrink-0"
              onClick={onCancel}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {isEditMode ? (
              <Input
                value={currentPreset.name}
                onChange={(e) => setCurrentPreset(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter preset name..."
                className="h-10 border-none bg-transparent p-0 text-xl font-bold text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 w-56 shrink-0"
              />
            ) : (
              <span className="text-xl font-bold text-foreground truncate max-w-[14rem] shrink-0">
                {currentPreset.name}
              </span>
            )}
            {isEditMode ? (
              <span className="px-2 py-0.5 rounded-md bg-accent/50 text-[10px] font-bold text-muted-foreground uppercase tracking-wider border border-border/50 shrink-0">
                Editing
              </span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 rounded-xl gap-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-all text-sm font-medium shrink-0"
                onClick={() => setIsEditMode(true)}
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isEditMode ? (
              <>
                <Button
                  size="sm"
                  className="h-9 px-3 rounded-xl gap-2 bg-foreground hover:bg-foreground/90 text-background shadow-md shadow-foreground/20 transition-all active:scale-95 text-sm font-bold"
                  onClick={() => setIsDesignerModalOpen(true)}
                >
                  <Sparkles className="w-4 h-4" />
                  Generate
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 p-0 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
                  onClick={() => setIsContrastModalOpen(true)}
                  title="Contrast"
                >
                  <Contrast className="w-4 h-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 p-0 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
                  onClick={() => setIsExtractOpen(true)}
                  title="Extract"
                >
                  <ImageIcon className="w-4 h-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-9 px-3 rounded-xl gap-2 transition-all text-sm font-medium ${
                    activeView === 'browse'
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                  onClick={() => setActiveView('browse')}
                >
                  <Library className="w-4 h-4" />
                  Browse
                </Button>

                <div className="w-px h-6 bg-border mx-1 shrink-0" />
              </>
            ) : null}

            {isEditMode ? (
              <Button
                size="sm"
                className="h-9 px-4 rounded-xl gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white transition-all active:scale-95 font-bold shadow-lg shadow-workspace-accent/20"
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving…' : 'Save Preset'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-3 rounded-xl gap-2 border-border bg-background hover:bg-accent transition-all font-semibold text-sm"
                onClick={() => setIsPreviewOpen(true)}
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            )}
          </div>
        </header>
      )}

      {/* ── Embedded Toolbar (no name, just actions) ────────────────────── */}
      {embedded && activeView !== 'browse' && (
        <div className="h-12 border-b border-border flex items-center justify-between px-4 bg-background/80 backdrop-blur-md z-30 sticky top-0 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          {/* Left: Global Format Switcher */}
          <div className="flex items-center px-2">
            <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-8 shrink-0 select-none">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                {currentPreset.colors[0]?.format || 'oklch'}
              </span>
              <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                <button
                  type="button"
                  onClick={() => handleGlobalFormatChange('up')}
                  className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                  title="Previous format"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => handleGlobalFormatChange('down')}
                  className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                  title="Next format"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 min-w-max px-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
              onClick={() => setIsContrastModalOpen(true)}
              title="Contrast"
            >
              <Contrast className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
              onClick={() => setIsExtractOpen(true)}
              title="Extract"
            >
              <ImageIcon className="w-4 h-4" />
            </Button>

            <Button
              size="sm"
              className="h-8 px-3 rounded-xl gap-2 bg-foreground hover:bg-foreground/90 text-background shadow-md shadow-foreground/20 transition-all active:scale-95 text-sm font-bold"
              onClick={() => {
                setDesignerDraftPreset(currentPreset);
                setIsDesignerModalOpen(true);
              }}
            >
              <Sparkles className="w-4 h-4" />
              Generate
            </Button>
          </div>
        </div>
      )}

      {/* ── Main Content ───────────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden relative bg-background">
        <AnimatePresence mode="wait">



          {/* ── Editor View ───────────────────────────────────────── */}
          {activeView === 'editor' && (
            <motion.div
              key="editor"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              {currentPreset.colors.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-6">
                    <Table2 className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">No colors yet</h3>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto mb-6">
                    Click Generate to create a palette, Browse to find a template, or add colors manually.
                  </p>
                  {isEditMode && (
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        className="rounded-xl gap-2"
                        onClick={() => setIsDesignerModalOpen(true)}
                      >
                        <Palette className="w-4 h-4" />
                        Generate Color
                      </Button>
                      <Button
                        className="rounded-xl gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white"
                        onClick={() => addColorAt(0)}
                      >
                        <Plus className="w-4 h-4" />
                        Add Color
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col overflow-hidden min-h-0">


                  <div className="flex-1 overflow-y-auto px-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {isEditMode ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                      >
                        <SortableContext
                          items={currentPreset.colors.map(c => c.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-1.5 py-3 px-1">
                            <AnimatePresence mode="popLayout">
                              {currentPreset.colors.map((color, index) => (
                                <ColorSwatch
                                  key={color.id}
                                  color={color}
                                  onColorChange={updateColor}
                                  onRemove={removeColor}
                                  onAddAfter={() => addColorAt(currentPreset.colors.findIndex(c => c.id === color.id) + 1)}
                                  index={index}
                                />
                              ))}
                            </AnimatePresence>
                          </div>
                        </SortableContext>
                        <DragOverlay dropAnimation={dropAnimation}>
                          {activeId ? (
                            <div className="bg-card border-border rounded-2xl opacity-95 border-2 shadow-2xl">
                              <ColorSwatch
                                color={currentPreset.colors.find(c => c.id === activeId)!}
                                onColorChange={() => { }}
                                onRemove={() => { }}
                                onAddAfter={() => { }}
                                index={0}
                              />
                            </div>
                          ) : null}
                        </DragOverlay>
                      </DndContext>
                    ) : (
                      <div className="space-y-1.5 py-3 px-1">
                        <AnimatePresence mode="popLayout">
                          {currentPreset.colors.map((color, index) => (
                            <ReadOnlyColorRow key={color.id} color={color} index={index} />
                          ))}
                        </AnimatePresence>
                      </div>
                    )}


                  </div>
                </div>
              )}
            </motion.div>
          )}



          {/* ── Browse View ──────────────────────────────────────────── */}
          {activeView === 'browse' && (
            <motion.div
              key="browse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              <div className="h-14 flex items-center justify-center px-6 shrink-0 border-b border-border relative w-full">
                <div className="flex items-center gap-3">
                  <div className="relative group" style={{ width: 280 }}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-foreground transition-colors" />
                    <Input
                      placeholder="Search templates…"
                      value={browseSearchQuery}
                      onChange={(e) => setBrowseSearchQuery(e.target.value)}
                      className="pl-9 h-9 bg-muted border-none text-foreground placeholder:text-muted-foreground rounded-xl focus:ring-1 focus:ring-workspace-accent/50 text-sm"
                    />
                  </div>
                   <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className={`h-9 w-9 rounded-xl transition-all border-border ${
                          selectedCategory !== 'All'
                            ? 'text-workspace-accent bg-workspace-accent/10 border-workspace-accent/30'
                            : 'bg-background hover:bg-accent text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Filter className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[240px] bg-popover border-border text-popover-foreground rounded-2xl p-2">
                      <DropdownMenuLabel className="text-muted-foreground text-[10px] uppercase tracking-widest px-3 py-2">Categories</DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-border" />
                      <div className="max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-border">
                        {categories.map(cat => (
                          <DropdownMenuItem
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className="flex items-center justify-between px-3 py-2 hover:bg-muted transition-colors cursor-pointer rounded-xl group"
                          >
                            <span className={`text-sm ${selectedCategory === cat ? 'text-workspace-accent font-bold' : 'group-hover:text-foreground'}`}>
                              {cat} <span className="text-xs text-muted-foreground ml-1 font-normal">({categoryCounts[cat] || 0})</span>
                            </span>
                            {selectedCategory === cat && <Check className="w-4 h-4 text-workspace-accent" />}
                          </DropdownMenuItem>
                        ))}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-border flex flex-col justify-between">
                {displayedTemplates.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 p-6">
                    {displayedTemplates.map((template, index) => (
                      <BrowseTemplateCard
                        key={template.id}
                        template={template}
                        index={index}
                        onPreview={(t) => setTemplateToPreview(t)}
                        onImport={(t) => {
                          applyTemplate(t);
                          toast.success(`'${t.name}' applied!`);
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <Search className="w-10 h-10 text-muted-foreground mb-4 opacity-20" />
                    <p className="text-muted-foreground font-medium">No templates found</p>
                    <p className="text-sm text-muted-foreground/60 mt-1">Try adjusting your search or category filter</p>
                  </div>
                )}

                {/* Infinite scroll sentinel */}
                {browseHasMore && (
                  <div ref={browseSentinelRef} className="flex items-center justify-center py-6">
                    {isBrowseLoadingMore && (
                      <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <div className="w-3.5 h-3.5 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                        Loading more...
                      </div>
                    )}
                  </div>
                )}
                {!browseHasMore && displayedTemplates.length > 0 && (
                  <div ref={browseSentinelRef} />
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      {/* ── Designer View Modal ────────────────────────────────────────── */}
      <Dialog open={isDesignerModalOpen} onOpenChange={(open) => {
        if (!open) setDesignerDraftPreset(null);
        setIsDesignerModalOpen(open);
      }}>
        <DialogContent className="max-w-[85vw] w-[900px] h-[600px] bg-background border border-border p-0 flex flex-col gap-0 overflow-hidden [&>button]:hidden [&>button]:text-foreground [&>button]:hover:bg-accent [&>button]:hover:text-foreground">
          <DialogHeader className="p-6 border-b border-border shrink-0 flex flex-row items-center justify-between">
            <div className="space-y-1.5">
              <DialogTitle className="text-foreground">Color Generator</DialogTitle>
              <DialogDescription className="text-muted-foreground">Generate and blend colors for your palette.</DialogDescription>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDesignerDraftPreset(null);
                  setIsDesignerModalOpen(false);
                }}
                className="rounded-xl border-border hover:bg-accent"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (designerDraftPreset) setCurrentPreset(designerDraftPreset);
                  setDesignerDraftPreset(null);
                  setIsDesignerModalOpen(false);
                }}
                className="rounded-xl bg-workspace-accent hover:bg-workspace-accent/90 text-white shadow-lg shadow-workspace-accent/20"
              >
                Confirm
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 flex flex-col overflow-hidden">
              {designerColors.length === 0 && !isGenerating ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-6">
                    <Palette className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">No palette yet</h3>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto mb-6">
                    Generate a palette or browse templates to get started.
                  </p>
                  {isEditMode && (
                    <Button
                      className="rounded-xl gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white"
                      onClick={generateAPIPalette}
                    >
                      <RefreshCw className="w-4 h-4" />
                      Generate Palette
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div
                    className={`flex-1 min-h-0 flex overflow-hidden transition-opacity duration-300 ${
                      isGenerating ? 'opacity-40 pointer-events-none' : 'opacity-100'
                    }`}
                  >
                    <AnimatePresence>
                      {designerColors.map((color, i) => (
                        <DesignerColorCard
                          key={color.id}
                          color={color}
                          index={i}
                          isEditMode={isEditMode}
                          isLocked={lockedIndices.has(i)}
                          onColorChange={updateColor}
                          toggleLock={toggleLock}
                          onRemove={removeColor}
                          onAddBlended={addBlendedColor}
                          isLast={i === designerColors.length - 1}
                          nextColor={designerColors[i + 1]?.hex}
                        />
                      ))}
                    </AnimatePresence>
                  </div>

                  {isEditMode && (
                    <div className="flex items-center shrink-0 border-t border-border">
                      <Button
                        variant="outline"
                        className="gap-2 flex-1 h-14 rounded-none border-0 bg-background hover:bg-accent transition-all font-bold text-base"
                         onClick={generateAPIPalette}
                        disabled={isGenerating}
                      >
                        {isGenerating ? <LoadingSpinner /> : <RefreshCw className="w-4 h-4" />}
                        {isGenerating ? 'Generating...' : 'Regenerate'}
                        {lockedIndices.size > 0 && !isGenerating && (
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            ({lockedIndices.size} locked)
                          </span>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Contrast Matrix Modal ───────────────────────────────────────── */}
      <Dialog open={isContrastModalOpen} onOpenChange={setIsContrastModalOpen}>
        <DialogContent className="max-w-[90vw] w-[1200px] max-h-[80vh] bg-background border border-border p-0 flex flex-col overflow-hidden [&>button]:text-foreground [&>button]:hover:bg-accent [&>button]:hover:text-foreground">
          <DialogTitle className="sr-only">Contrast Matrix</DialogTitle>
          <div className="flex-1 flex flex-col overflow-y-auto p-6">
              {currentPreset.colors.length >= 2 ? (
                <ContrastMatrixTable colors={currentPreset.colors} />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12 h-full">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-6">
                    <Contrast className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Not enough colors</h3>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                    Add at least 2 colors to view the contrast matrix.
                  </p>
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Extract from Image Modal ──────────────────────────────────── */}
      <Dialog open={isExtractOpen} onOpenChange={(open) => {
        setIsExtractOpen(open);
        if (!open) {
          setExtractPreview(null);
          setExtractPaletteResult(null);
          setExtractSeed(0);
          setExtractAnalyzing(false);
          setIsExtractApplying(false);
          extractImgRef.current = null;
        }
      }}>
        <DialogContent className="max-w-lg bg-popover border border-border text-popover-foreground [&>button]:text-foreground [&>button]:hover:text-foreground [&>button]:hover:bg-muted [&>button]:bg-transparent [&>button]:border-0 [&>button]:p-1 [&>button]:w-6 [&>button]:h-6 [&>button]:flex [&>button]:items-center [&>button]:justify-center">
          <DialogHeader>
            <DialogTitle className="text-foreground">Extract from Image</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Upload an image to automatically extract a color palette.
            </DialogDescription>
          </DialogHeader>

          {!extractPreview ? (
            /* ── Drop Zone ─────────────────────────────────────── */
            <div className="py-4">
              <input
                ref={extractInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleExtractFile(file);
                }}
              />
              <div
                className="border-2 border-dashed border-border hover:border-workspace-accent/50 bg-muted/20 hover:bg-workspace-accent/5 rounded-2xl p-10 text-center cursor-pointer transition-all duration-300"
                onClick={() => extractInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-workspace-accent', 'bg-workspace-accent/5'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-workspace-accent', 'bg-workspace-accent/5'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-workspace-accent', 'bg-workspace-accent/5');
                  if (e.dataTransfer.files[0]) handleExtractFile(e.dataTransfer.files[0]);
                }}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-workspace-accent/10 flex items-center justify-center">
                    <Upload className="w-7 h-7 text-workspace-accent" />
                  </div>
                  <p className="text-base font-bold text-foreground">Drop your image here</p>
                  <p className="text-sm text-muted-foreground">or click to browse · PNG, JPG, WebP</p>
                </div>
              </div>
            </div>
          ) : (
            /* ── Results: Preview + Palette Explorer ───────────── */
            <div className="space-y-4 py-2">
              {/* Image preview */}
              <div className="rounded-xl overflow-hidden border border-border bg-card">
                <img src={extractPreview} alt="Uploaded" className="w-full object-cover max-h-44" />
              </div>

              {extractAnalyzing && (
                <div className="py-4">
                  <LoadingSpinner />
                  <p className="text-sm text-muted-foreground text-center mt-2">Analyzing colors…</p>
                </div>
              )}

              {extractPaletteResult && (
                <>
                  {/* Quick preview bar */}
                  <div className="rounded-xl overflow-hidden border border-border">
                    <div className="flex h-12">
                      {[
                        ...extractPaletteResult.primary,
                        ...extractPaletteResult.accent,
                        ...extractPaletteResult.neutral,
                      ].slice(0, 6).map((c, i) => (
                        <div key={i} className="flex-1 relative group" style={{ backgroundColor: c.hex }}>
                          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.hsl.l > 55 ? '#000' : '#fff' }}>
                            {c.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Palette Explorer */}
                  <div className="p-4 rounded-xl bg-card border border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">Palette Explorer</p>
                      <button
                        onClick={() => {
                          const newSeed = Math.floor(Math.random() * 100);
                          setExtractSeed(newSeed);
                          if (extractImgRef.current) {
                            const result = extractPalette(extractImgRef.current, 6, newSeed);
                            setExtractPaletteResult(result);
                          }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-workspace-accent/10 text-workspace-accent text-[11px] font-bold hover:bg-workspace-accent/20 transition-colors"
                      >
                        <Shuffle className="w-3 h-3" />
                        Randomize
                      </button>
                    </div>
                    <Slider
                      value={[extractSeed]}
                      onValueChange={(values) => {
                        const newSeed = values[0];
                        setExtractSeed(newSeed);
                        if (extractImgRef.current) {
                          const result = extractPalette(extractImgRef.current, 6, newSeed);
                          setExtractPaletteResult(result);
                        }
                      }}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-[10px] text-muted-foreground text-center">
                      Drag to explore different color combinations from your image
                    </p>
                  </div>

                  {/* Apply Button */}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setExtractPreview(null);
                        setExtractPaletteResult(null);
                        setExtractSeed(0);
                        setIsExtractApplying(false);
                        extractImgRef.current = null;
                      }}
                      className="bg-card hover:bg-muted text-foreground border border-border transition-all active:scale-95 rounded-xl"
                    >
                      Try Another
                    </Button>
                    <Button
                      disabled={isExtractApplying}
                      onClick={async () => {
                        if (!extractPaletteResult) return;
                        setIsExtractApplying(true);
                        
                        try {
                          const allExtracted = [
                            ...extractPaletteResult.primary,
                            ...extractPaletteResult.accent,
                            ...extractPaletteResult.neutral,
                          ].slice(0, 6);

                          const roleNames = ['Primary', 'Secondary', 'Accent', 'Neutral 1', 'Light', 'Dark'];

                          const fetchedColors = await Promise.all(
                            allExtracted.map(async (c, i) => {
                              const cleanHex = c.hex.replace('#', '');
                              try {
                                const res = await fetch(`https://www.thecolorapi.com/id?hex=${cleanHex}`);
                                const data = await res.json();
                                const fetchedName = data.name?.value || roleNames[i] || c.name;
                                return { c, i, fetchedName };
                              } catch (err) {
                                return { c, i, fetchedName: roleNames[i] || c.name };
                              }
                            })
                          );

                          const newColors: Color[] = fetchedColors.map(({ c, fetchedName }) => ({
                            id: generateId(),
                            name: fetchedName,
                            hex: c.hex.startsWith('#') ? c.hex.toUpperCase() : `#${c.hex}`.toUpperCase(),
                            variable: `--${fetchedName.toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '')}`,
                            format: 'oklch' as const,
                          }));

                          setCurrentPreset(prev => ({
                            ...prev,
                            colors: newColors,
                            updatedAt: new Date().toISOString(),
                          }));

                          setIsExtractOpen(false);
                          setExtractPreview(null);
                          setExtractPaletteResult(null);
                          setExtractSeed(0);
                          extractImgRef.current = null;
                          toast.success('Colors extracted and applied!');
                        } catch (error) {
                          toast.error('Failed to apply colors');
                        } finally {
                          setIsExtractApplying(false);
                        }
                      }}
                      className="bg-workspace-accent text-white hover:bg-workspace-accent/90 transition-all active:scale-95 font-bold rounded-xl gap-2"
                    >
                      {isExtractApplying ? <LoadingSpinner className="text-white" /> : null}
                      {isExtractApplying ? 'Applying...' : 'Apply to Preset'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Preview & Export Dialog ───────────────────────────────────── */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col bg-popover border border-border text-popover-foreground [&>button]:text-foreground [&>button]:hover:text-foreground [&>button]:hover:bg-muted [&>button]:bg-transparent [&>button]:border-0 [&>button]:p-1 [&>button]:w-6 [&>button]:h-6 [&>button]:flex [&>button]:items-center [&>button]:justify-center">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-foreground">Preview & Export</DialogTitle>
            <DialogDescription className="text-muted-foreground">Export your color preset as CSS variables or JSON.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="css" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="shrink-0 bg-muted border border-border rounded-xl mb-4">
              <TabsTrigger value="css">CSS Variables</TabsTrigger>
              <TabsTrigger value="json">JSON Data</TabsTrigger>
            </TabsList>
            <TabsContent value="css" className="flex-1 flex flex-col overflow-hidden m-0">
              <div className="flex justify-between items-center mb-4 shrink-0">
                <h3 className="text-base font-bold text-foreground">CSS Variables</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(generateCSSPreview())} className="text-foreground border-border bg-background hover:bg-muted h-9 px-4 rounded-xl transition-all active:scale-95">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy CSS
                  </Button>
                  <Button size="sm" onClick={downloadCSS} className="bg-workspace-accent hover:bg-workspace-accent/90 text-white h-9 px-4 rounded-xl font-bold transition-all active:scale-95">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
              <div className="flex-1 bg-black/80 text-green-400 border-border p-5 rounded-2xl font-mono text-sm overflow-auto border scrollbar-thin scrollbar-thumb-border">
                <pre>{generateCSSPreview()}</pre>
              </div>
            </TabsContent>
            <TabsContent value="json" className="flex-1 flex flex-col overflow-hidden m-0">
              <div className="flex justify-between items-center mb-4 shrink-0">
                <h3 className="text-base font-bold text-foreground">JSON Data</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(generateJSONPreview())} className="text-foreground border-border bg-background hover:bg-muted h-9 px-4 rounded-xl transition-all active:scale-95">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy JSON
                  </Button>
                  <Button size="sm" onClick={downloadJSON} className="bg-workspace-accent hover:bg-workspace-accent/90 text-white h-9 px-4 rounded-xl font-bold transition-all active:scale-95">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
              <div className="flex-1 bg-black/80 text-sky-400 border-border p-5 rounded-2xl font-mono text-sm overflow-auto border scrollbar-thin scrollbar-thumb-border">
                <pre>{generateJSONPreview()}</pre>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ── Template Preview Dialog ───────────────────────────────────── */}
      <Dialog open={!!templateToPreview} onOpenChange={(open) => !open && setTemplateToPreview(null)}>
        <DialogContent className="bg-popover border border-border text-popover-foreground [&>button]:text-foreground [&>button]:hover:text-foreground [&>button]:hover:bg-muted [&>button]:bg-transparent [&>button]:border-0 [&>button]:p-1 [&>button]:w-6 [&>button]:h-6 [&>button]:flex [&>button]:items-center [&>button]:justify-center">
          {templateToPreview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-foreground">
                  {templateToPreview.logoSvg ? (
                    <img src={templateToPreview.logoSvg} alt="Logo" className="w-8 h-8 flex-shrink-0 object-contain" />
                  ) : (
                    <div className="w-8 h-8 rounded shrink-0 bg-muted flex items-center justify-center">
                      <Library className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  {templateToPreview.name}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {templateToPreview.description || 'Brand Color Palette'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border border-border rounded-lg overflow-hidden bg-board-column">
                  <div className="grid grid-cols-2 bg-muted border-b border-border">
                    <div className="px-4 py-3 text-sm font-medium text-foreground border-r border-border">Colors</div>
                    <div className="px-4 py-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Color Codes</span>
                      <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-8 shrink-0 select-none">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                          {previewFormat}
                        </span>
                        <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              const fmts = ['oklch','hex','rgb','hsl'] as const;
                              const idx = fmts.indexOf(previewFormat as any);
                              setPreviewFormat(fmts[idx === 0 ? fmts.length - 1 : idx - 1]);
                            }}
                            className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                            title="Previous format"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const fmts = ['oklch','hex','rgb','hsl'] as const;
                              const idx = fmts.indexOf(previewFormat as any);
                              setPreviewFormat(fmts[idx === fmts.length - 1 ? 0 : idx + 1]);
                            }}
                            className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                            title="Next format"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {templateToPreview.colors.map((color, idx) => (
                      <div key={idx} className="grid grid-cols-2 bg-board-column hover:bg-muted transition-colors">
                        <div className="px-4 py-3 border-r border-border flex items-center gap-3">
                          <div className="w-8 h-8 rounded border border-border" style={{ backgroundColor: color.hex }} />
                          <div className="font-medium text-foreground">{color.name}</div>
                        </div>
                        <div className="px-4 py-3 flex items-center justify-between">
                          <div className="text-sm font-mono font-semibold text-foreground">{getColorValue(color.hex, previewFormat)}</div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(getColorValue(color.hex, previewFormat));
                              toast.success(`${color.name} copied!`);
                            }}
                            className="p-1 h-6 w-6 bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent border-0 flex-shrink-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setTemplateToPreview(null)} className="bg-board-column hover:bg-muted text-foreground border border-border transition-all active:scale-95">
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      applyTemplate(templateToPreview);
                      setTemplateToPreview(null);
                      toast.success(`'${templateToPreview.name}' palette applied!`);
                    }}
                    className="bg-workspace-accent text-white hover:bg-workspace-accent/90 transition-all active:scale-95"
                  >
                    Import This Template
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  // ── Image extract file handler ────────────────────────────────────────
  function handleExtractFile(file: File) {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      setExtractPreview(src);
      setExtractAnalyzing(true);
      setExtractPaletteResult(null);
      setExtractSeed(0);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        extractImgRef.current = img;
        requestAnimationFrame(() => {
          const result = extractPalette(img, 6, 0);
          setExtractPaletteResult(result);
          setExtractAnalyzing(false);
        });
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  }
};

// ─── Read-Only Color Row ────────────────────────────────────────────────────
interface ReadOnlyColorRowProps {
  color: Color;
  index: number;
}

const ReadOnlyColorRow = React.memo(React.forwardRef<HTMLDivElement, ReadOnlyColorRowProps>(({ color, index }, ref) => {
  const [format, setFormat] = React.useState(color.format || 'oklch');
  const [isExpanded, setIsExpanded] = React.useState(false);

  const colorValue = getColorValue(color.hex, format);

  const shades = color.shadesEnabled ? (color.customShades || generateShades(color.hex, color.shadesCount || 3)) : [];
  const tints = color.tintsEnabled ? (color.customTints || generateTints(color.hex, color.tintsCount || 3)) : [];
  const transparencies = color.transparencyEnabled ? generateTransparencies(color.hex, color.transparencyCount || 5) : [];
  const hasExpandable = shades.length > 0 || tints.length > 0 || transparencies.length > 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden transition-all duration-200"
    >
      <div
        className="grid gap-2 items-center px-4 py-3 hover:bg-muted/30 transition-colors"
        style={{ gridTemplateColumns: '32px 1fr 32px 24px' }}
      >
        <div className="flex justify-start">
          <div
            className="w-8 h-8 rounded-full border border-border/50 shadow-sm"
            style={{ backgroundColor: color.hex }}
          />
        </div>
        <span className="font-mono text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg truncate">{color.variable}</span>
        <div className="flex justify-end min-w-0">
          <CopyButton value={colorValue} />
        </div>

        <div className="flex justify-center items-center">
          {hasExpandable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 h-6 w-6 bg-transparent border-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
            </Button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="bg-muted/20 border-t border-border px-8 py-6">
          <div className="space-y-6">
            {shades.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Shades</p>
                <div className="space-y-2">
                  {shades.map((shade, idx) => {
                    return (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-card rounded-lg border border-border">
                        <div className="w-8 h-8 rounded-full border-2 border-border flex-shrink-0" style={{ backgroundColor: shade.hex }} />
                        <span className="font-mono text-xs text-muted-foreground flex-1 truncate">{shade.variable}</span>
                        <div className="flex justify-end shrink-0">
                          <CopyButton value={getColorValue(shade.hex, format)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {tints.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Tints</p>
                <div className="space-y-2">
                  {tints.map((tint, idx) => {
                    return (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-card rounded-lg border border-border">
                        <div className="w-8 h-8 rounded-full border-2 border-border flex-shrink-0" style={{ backgroundColor: tint.hex }} />
                        <span className="font-mono text-xs text-muted-foreground flex-1 truncate">{tint.variable}</span>
                        <div className="flex justify-end shrink-0">
                          <CopyButton value={getColorValue(tint.hex, format)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {transparencies.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Transparency</p>
                <div className="space-y-2">
                  {transparencies.map((t, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-card rounded-lg border border-border">
                      <div className="w-8 h-8 rounded-full border-2 border-border flex-shrink-0 relative overflow-hidden">
                        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-conic-gradient(#ccc 0% 25%, transparent 0% 50%)', backgroundSize: '8px 8px' }} />
                        <div className="absolute inset-0" style={{ backgroundColor: color.hex, opacity: t.opacity / 100 }} />
                      </div>
                      <span className="font-mono text-xs text-muted-foreground flex-1 truncate">{t.variable}</span>
                      <div className="flex justify-end shrink-0">
                        <CopyButton value={getTransparencyColorValue(color.hex, t.opacity, color.format || 'oklch')} />
                      </div>
                      <div className="flex-1" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}));
ReadOnlyColorRow.displayName = 'ReadOnlyColorRow';

// ─── WCAG Badge (Inline compact version) ────────────────────────────────────
const wcagBadgeColors: Record<WcagLevel, string> = {
  'AAA': 'bg-emerald-500/15 text-emerald-500',
  'AA': 'bg-sky-500/15 text-sky-500',
  'AA Large': 'bg-amber-500/15 text-amber-500',
  'Fail': 'bg-red-500/15 text-red-500',
};

// ─── Contrast Matrix Table ──────────────────────────────────────────────────
interface ContrastMatrixTableProps {
  colors: Color[];
}

const ContrastMatrixTable: React.FC<ContrastMatrixTableProps> = ({ colors }) => {
  if (colors.length < 2) return null;
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };

  // Background colors = vertical (rows)
  const bgColors = colors;
  // Typography colors = horizontal (columns) + White + Black
  const typoColors = colors;

  return (
    <div className="mx-0">
      <div className="flex items-baseline gap-3 mb-4">
        <h3 className="text-base font-bold text-foreground">Contrast Matrix</h3>
        <p className="text-xs text-muted-foreground">WCAG compliance between color pairs</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border pb-2">
        <table className="text-sm w-full bg-card min-w-max">
          <thead>
            <tr className="border-b border-border">
              <th className="p-5 text-center align-middle">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">BG \ Type</span>
              </th>
              <th className="p-5 text-center align-middle">
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="w-8 h-8 rounded border border-border" style={{ backgroundColor: '#ffffff' }} />
                  <span className="text-xs font-medium text-muted-foreground">White</span>
                </div>
              </th>
              <th className="p-5 text-center align-middle">
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="w-8 h-8 rounded border border-border" style={{ backgroundColor: '#000000' }} />
                  <span className="text-xs font-medium text-muted-foreground">Black</span>
                </div>
              </th>
              {typoColors.map(c => (
                <th key={c.id} className="p-5 text-center align-middle">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="w-8 h-8 rounded border border-border/50" style={{ backgroundColor: c.hex }} />
                    <span className="text-xs font-medium text-muted-foreground truncate max-w-[80px]">{c.name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bgColors.map((bgColor) => {
              const bgRgb = hexToRgbValues(bgColor.hex);
              return (
                <tr key={bgColor.id} className="border-b border-border/50 last:border-b-0 hover:bg-muted/30 transition-colors">
                  <td className="p-5 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-md border border-border/50 flex-shrink-0" style={{ backgroundColor: bgColor.hex }} />
                      <span className="text-sm font-bold text-foreground">{bgColor.name}</span>
                    </div>
                  </td>
                  <td className="p-5 text-center">
                    <WcagBadgeCell ratio={contrastRatio(bgRgb, white)} />
                  </td>
                  <td className="p-5 text-center">
                    <WcagBadgeCell ratio={contrastRatio(bgRgb, black)} />
                  </td>
                  {typoColors.map((typoColor) => (
                    <td key={typoColor.id} className="p-5 text-center">
                      {bgColor.id === typoColor.id ? (
                        <span className="text-muted-foreground/40 text-lg">—</span>
                      ) : (
                        <WcagBadgeCell ratio={contrastRatio(bgRgb, hexToRgbValues(typoColor.hex))} />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WcagBadgeCell = ({ ratio }: { ratio: number }) => {
  const level = wcagLevel(ratio);
  return (
    <div className="flex items-center justify-center gap-2.5">
      <span className={`text-[10px] sm:text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap ${wcagBadgeColors[level]}`}>
        {level}
      </span>
      <span className="text-sm sm:text-base text-foreground font-black font-mono tracking-tight">{ratio.toFixed(1)}</span>
    </div>
  );
};

// ─── Browse Template Card (defined outside to keep reference stable) ───────
interface BrowseTemplateCardProps {
  template: TemplatePreset;
  index: number;
  onPreview: (t: TemplatePreset) => void;
  onImport: (t: TemplatePreset) => void;
}

const BrowseTemplateCard = React.memo(({ template, index, onPreview, onImport }: BrowseTemplateCardProps) => {
  const [liked, setLiked] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      className="rounded-xl bg-card border border-border/50 shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col overflow-hidden"
      onClick={() => onPreview(template)}
    >
      <div className="p-4 pb-3">
        <div className="flex items-start gap-3">
          {template.logoSvg ? (
            <img src={template.logoSvg} alt={template.name} className="w-9 h-9 flex-shrink-0 object-contain rounded-lg mt-0.5" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
              <Library className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="font-semibold text-sm text-foreground leading-tight truncate">{template.name}</h4>
              <span className="text-[11px] text-muted-foreground shrink-0">{template.category}</span>
            </div>
            {template.description && (
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{template.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className="h-16 rounded-lg overflow-hidden flex">
          {template.colors.map((c, idx) => (
            <div
              key={idx}
              className="flex-1 h-full"
              style={{ backgroundColor: c.hex }}
              title={`${c.name}: ${c.hex}`}
            />
          ))}
        </div>
      </div>

      <div className="p-4 pt-2 flex justify-between items-center mt-auto">
        <div className="flex gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(template); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-muted/80 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLiked(prev => !prev); }}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
              liked
                ? 'bg-rose-50 text-rose-500 hover:bg-rose-100'
                : 'bg-muted/80 text-muted-foreground hover:text-rose-400 hover:bg-muted'
            }`}
            title="Favorite"
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
          </button>
        </div>
        <Button
          size="sm"
          onClick={(e) => { e.stopPropagation(); onImport(template); }}
          className="h-8 px-3 rounded-lg bg-foreground/80 text-background hover:bg-foreground text-xs font-semibold gap-1.5 opacity-70 hover:opacity-100 transition-all active:scale-95"
        >
          <Download className="w-3 h-3" />
          Import
        </Button>
      </div>
    </motion.div>
  );
});
BrowseTemplateCard.displayName = 'BrowseTemplateCard';
